import { Injectable } from "@angular/core";
import { EntityService } from "@app/entity/services/entity.service";
import { AlulaReceiverBind } from "@app/project/model/alula-receiver-bind";
import { AlulaReceiverBindDbService } from "@app/project/services/alula-receiver-bind-db.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { checkCurrentID } from "@app/shared/resources/data-actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable } from "rxjs";
import { concatMap, map, mergeMap, switchMap } from "rxjs/operators";
import { AlulaReceiverActions, AlulaReceiverBindActions, DataActions } from "../actions";

const toAction = DataActions.toAction2();

@Injectable()
export class AlulaReceiverBindEffects {
    readonly entityName = "Communication Path Bind";

    constructor(
        private actions$: Actions,
        private alulaReceiverBindDbService: AlulaReceiverBindDbService,
        private readonly entityService: EntityService
    ) {}

    wrapResult(this: void, source$: Observable<AlulaReceiverBind>) {
        return source$.pipe(map(result => ({ bind: result })));
    }

    wrapBulkResult(this: void, source$: Observable<AlulaReceiverBind[]>) {
        return source$.pipe(
            map(result => {
                const binds = {};
                result.forEach(bind => {
                    if (!binds[bind.receiverGroupId]) {
                        binds[bind.receiverGroupId] = [bind];
                    } else {
                        binds[bind.receiverGroupId] = [...binds[bind.receiverGroupId], bind].sort((a, b) =>
                            a.priority > b.priority ? 1 : -1
                        );
                    }
                });
                return { binds };
            })
        );
    }

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverBindActions.load),
            switchMap(({ dealerId }) =>
                toAction(
                    this.alulaReceiverBindDbService
                        .getAlulaReceiverBindsByDealerId(dealerId)
                        .pipe(this.wrapBulkResult, checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), dealerId)),
                    AlulaReceiverBindActions.loadSuccess,
                    AlulaReceiverBindActions.loadFailure
                )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverBindActions.create),
            concatMap(({ receiverBind, callback }) =>
                toAction(
                    this.alulaReceiverBindDbService
                        .createAlulaReceiverBind(receiverBind)
                        .pipe(this.wrapResult, checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), receiverBind?.dealerId)),
                    AlulaReceiverBindActions.createSuccess,
                    AlulaReceiverBindActions.createFailure,
                    callback
                )
            )
        )
    );

    createFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverBindActions.createFailure),
            map(DataActions.handleErrorMessage2(ActionWord.created, this.entityName))
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverBindActions.update),
            switchMap(({ receiverBind, callback }) =>
                toAction(
                    this.alulaReceiverBindDbService
                        .updateAlulaReceiverBind(receiverBind, receiverBind.id)
                        .pipe(this.wrapResult, checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), receiverBind?.dealerId)),
                    AlulaReceiverBindActions.updateSuccess,
                    AlulaReceiverBindActions.updateFailure,
                    callback
                )
            )
        )
    );

    updateFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverBindActions.updateFailure),
            map(DataActions.handleErrorMessage2(ActionWord.saved, this.entityName))
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverBindActions.deleteItem),
            concatMap(({ receiverBind, receiver, callback }) =>
                toAction(
                    this.alulaReceiverBindDbService.deleteAlulaReceiverBind(receiverBind?.id).pipe(
                        map(_ => ({ receiverBind, receiver, callback })),
                        checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), receiverBind?.dealerId)
                    ),
                    AlulaReceiverBindActions.deleteSuccess,
                    AlulaReceiverBindActions.deleteFailure,
                    callback
                )
            )
        )
    );

    deleteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverBindActions.deleteSuccess),
            mergeMap(result => {
                const actionArr = [DataActions.handleSuccessMessage2(ActionWord.removed, null, this.entityName)()];
                if (result?.receiver) {
                    actionArr.push(AlulaReceiverActions.deleteItem({ receiver: result.receiver, callback: result.callback }));
                }
                return actionArr;
            })
        )
    );

    deleteFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverBindActions.deleteFailure),
            map(DataActions.handleErrorMessage2(ActionWord.removed, this.entityName))
        )
    );
}
